import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Landmark, Plus, Trash2, Save, Loader2, User, Search, X, ChevronDown, ExternalLink, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import RichTextEditor from '@/components/RichTextEditor';
import { API } from '@/App';

const ROLE_OPTIONS = [
  'Président(e)', 'Vice-Président(e)', 'Secrétaire', 'Secrétaire Générale',
  'Trésorier(ère)', 'Trésorière', 'Présidente', 'Vice-Présidente',
  'Membre du CA', 'Membre', 'Suppléant(e)',
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserSearchSelector = ({ onSelect, existingIds = [] }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/users/search-for-bureau`, { params: { q: query } });
        setResults(res.data.filter(u => !existingIds.includes(u.id)));
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, existingIds]);

  return (
    <div ref={ref} className="relative" data-testid="user-search-selector">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Rechercher un membre par nom..."
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none"
          data-testid="bureau-member-search"
        />
        {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-gray-200 shadow-xl max-h-60 overflow-y-auto" data-testid="search-results-dropdown">
          {results.map(user => (
            <button
              key={user.id}
              onClick={() => { onSelect(user); setQuery(''); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#eaf6fc] transition-colors text-left"
              data-testid={`select-user-${user.id}`}
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0">
                {user.profile_image ? (
                  <img src={`${BACKEND_URL}${user.profile_image}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">Dr {user.first_name} {user.last_name}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && query.length >= 2 && results.length === 0 && !searching && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-gray-200 shadow-xl p-4 text-center">
          <p className="text-sm text-gray-400">Aucun membre trouvé</p>
        </div>
      )}
    </div>
  );
};

const BureauNationalPage = () => {
  const navigate = useNavigate();
  const [bureauMembers, setBureauMembers] = useState([]);
  const [councilMembers, setCouncilMembers] = useState([]);
  const [aboutText, setAboutText] = useState({ paragraphs: [''] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('bureau');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [bureauRes, aboutRes] = await Promise.all([
        axios.get(`${API}/site-content/bureau`),
        axios.get(`${API}/site-content/bureau_intro`),
      ]);
      const bd = bureauRes.data?.data || {};
      const bureau = (bd.members || []).map(m => ({
        user_id: m.user_id || '',
        name: m.name || '',
        role: m.role || 'Membre du CA',
        photo_url: m.photo_url || '',
      }));
      const council = (bd.council_members || []).map(m => ({
        user_id: m.user_id || '',
        name: m.name || '',
        role: m.role || 'Membre du CA',
        photo_url: m.photo_url || '',
      }));
      setBureauMembers(bureau);
      setCouncilMembers(council);
      if (aboutRes.data?.data) setAboutText(aboutRes.data.data);
    } catch { /* defaults */ }
    setLoading(false);
  };

  const saveBureau = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/bureau/save`, {
        members: bureauMembers,
        council_members: councilMembers,
        council_title: "Le conseil d'administration",
      });
      toast.success('Bureau mis à jour');
      fetchData();
    } catch { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  const saveAbout = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-content/bureau_intro`, { data: { paragraphs: aboutText.paragraphs } });
      toast.success('Texte de la page « Le bureau » mis à jour');
    } catch { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  const addMemberFromSearch = (user, list, setter) => {
    setter([...list, {
      user_id: user.id,
      name: `Dr ${user.first_name} ${user.last_name}`,
      role: ROLE_OPTIONS[0],
      photo_url: user.profile_image || '',
    }]);
  };

  const updateMember = (list, setter, idx, field, value) => {
    const updated = [...list];
    updated[idx] = { ...updated[idx], [field]: value };
    setter(updated);
  };

  const removeMember = (list, setter, idx) => {
    setter(list.filter((_, i) => i !== idx));
  };

  const getExistingIds = (list) => list.map(m => m.user_id).filter(Boolean);

  const MemberRow = ({ member, idx, list, setter }) => {
    const linked = !!member.user_id;
    const goToProfile = (e) => {
      e?.stopPropagation();
      if (linked) navigate(`/admin/members?user=${member.user_id}`);
    };
    return (
      <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 group" data-testid={`bureau-member-row-${idx}`}>
        <button
          onClick={goToProfile}
          disabled={!linked}
          className={`w-10 h-10 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white overflow-hidden flex-shrink-0 ${linked ? 'cursor-pointer hover:ring-2 hover:ring-[#35AFE4]/50 transition-all' : 'cursor-default'}`}
          title={linked ? 'Voir le profil' : 'Membre non lié'}
          data-testid={`bureau-avatar-${idx}`}
        >
          {member.photo_url ? (
            <img src={member.photo_url.startsWith('http') ? member.photo_url : `${BACKEND_URL}${member.photo_url}`} alt="" className="w-full h-full object-cover" />
          ) : (
            <User size={16} />
          )}
        </button>
        <div className="flex-1 min-w-0">
          {linked ? (
            <button onClick={goToProfile}
              className="text-sm font-semibold text-gray-900 truncate hover:text-[#35AFE4] transition-colors flex items-center gap-1.5"
              data-testid={`bureau-name-${idx}`}>
              {member.name}
              <ExternalLink size={11} className="opacity-50 group-hover:opacity-100 transition-opacity" />
            </button>
          ) : (
            <p className="text-sm font-semibold text-gray-400 truncate">{member.name}</p>
          )}
          {linked ? (
            <p className="text-[10px] text-emerald-600 flex items-center gap-1"><Link2 size={9} /> Lié au compte</p>
          ) : (
            <p className="text-[10px] text-orange-500">Non lié — supprimez et ré-ajoutez via la recherche</p>
          )}
        </div>
        <input
          list={`roles-bureau-${idx}`}
          value={member.role}
          onChange={e => updateMember(list, setter, idx, 'role', e.target.value)}
          placeholder="Rôle"
          className="px-2 py-2 rounded-lg border border-gray-200 text-sm bg-white w-44 flex-shrink-0"
          data-testid={`bureau-member-role-${idx}`}
        />
        <datalist id={`roles-bureau-${idx}`}>
          {ROLE_OPTIONS.map(r => <option key={r} value={r} />)}
        </datalist>
        <button
          onClick={() => removeMember(list, setter, idx)}
          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
          data-testid={`remove-bureau-member-${idx}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    );
  };

  if (loading) return <AdminLayout><div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-5xl mx-auto" data-testid="bureau-national-admin">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#c9a227] to-[#a07d1c] flex items-center justify-center">
              <Landmark size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Contenu du site</h1>
              <p className="text-xs text-gray-500">Bureau national & page « Le bureau »</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
          {[
            { id: 'bureau', label: 'Bureau national' },
            { id: 'council', label: "Conseil d'administration" },
            { id: 'about', label: 'Texte de la page' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === t.id ? 'bg-white text-[#1e3a5f] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Bureau Tab */}
        {activeTab === 'bureau' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Membres du bureau ({bureauMembers.length})</p>
              <button onClick={saveBureau} disabled={saving}
                className="flex items-center gap-1 px-4 py-1.5 bg-[#1e3a5f] text-white rounded-lg text-xs font-bold hover:bg-[#162d4a] disabled:opacity-50" data-testid="save-bureau">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Sauvegarder
              </button>
            </div>

            {/* Member search selector */}
            <div className="bg-white rounded-xl border border-dashed border-[#35AFE4] p-4">
              <p className="text-xs text-gray-500 mb-2 font-medium">Ajouter un membre du bureau (rechercher parmi les membres de l'app) :</p>
              <UserSearchSelector
                onSelect={(user) => addMemberFromSearch(user, bureauMembers, setBureauMembers)}
                existingIds={getExistingIds(bureauMembers)}
              />
            </div>

            {/* Members list */}
            <div className="space-y-2">
              {bureauMembers.map((m, i) => <MemberRow key={`${m.user_id || i}`} member={m} idx={i} list={bureauMembers} setter={setBureauMembers} />)}
            </div>
            {bureauMembers.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <User size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">Aucun membre du bureau. Utilisez la recherche ci-dessus.</p>
              </div>
            )}
          </div>
        )}

        {/* Council Tab */}
        {activeTab === 'council' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Membres du conseil ({councilMembers.length})</p>
              <button onClick={saveBureau} disabled={saving}
                className="flex items-center gap-1 px-4 py-1.5 bg-[#1e3a5f] text-white rounded-lg text-xs font-bold hover:bg-[#162d4a] disabled:opacity-50" data-testid="save-council">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Sauvegarder
              </button>
            </div>

            {/* Member search selector */}
            <div className="bg-white rounded-xl border border-dashed border-[#35AFE4] p-4">
              <p className="text-xs text-gray-500 mb-2 font-medium">Ajouter un membre du conseil :</p>
              <UserSearchSelector
                onSelect={(user) => addMemberFromSearch(user, councilMembers, setCouncilMembers)}
                existingIds={getExistingIds(councilMembers)}
              />
            </div>

            <div className="space-y-2">
              {councilMembers.map((m, i) => <MemberRow key={`${m.user_id || i}`} member={m} idx={i} list={councilMembers} setter={setCouncilMembers} />)}
            </div>
            {councilMembers.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <User size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">Aucun membre du CA. Utilisez la recherche ci-dessus.</p>
              </div>
            )}
          </div>
        )}

        {/* Bureau page text (separate from the homepage text) */}
        {activeTab === 'about' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-semibold text-gray-700">Texte de présentation de la page « Le bureau »</p>
                <p className="text-xs text-gray-400">Le texte de l'accueil se modifie à part, dans Site → Page d'accueil.</p>
              </div>
              <button onClick={saveAbout} disabled={saving}
                className="flex items-center gap-1 px-4 py-1.5 bg-[#1e3a5f] text-white rounded-lg text-xs font-bold hover:bg-[#162d4a] disabled:opacity-50" data-testid="save-about">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Sauvegarder
              </button>
            </div>
            <RichTextEditor
              value={(aboutText.paragraphs || []).map(p => (p.trim().startsWith('<') ? p : `<p>${p}</p>`)).join('')}
              onChange={val => setAboutText(prev => ({ ...prev, paragraphs: [val] }))}
              placeholder="Présentation de la SFOALC sur la page Le bureau..."
              height="320px"
            />
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BureauNationalPage;
